import React from 'react';
import { Box, Paper, Typography, Avatar } from '@mui/material';

interface TestimonialCardProps {
  quote: string;
  author: string;
  role?: string;
  company?: string;
  avatar: string; // Initials or image URL
  avatarColor?: string;
  size?: 'full' | 'compact';
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  quote,
  author,
  role,
  company,
  avatar,
  avatarColor = '#0a7ca0',
  size = 'full'
}) => {
  const isFullSize = size === 'full';
  
  // Generate a color based on author name if no color provided
  const getAvatarColor = () => {
    if (avatarColor) return avatarColor;
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#9C27B0', '#FF9800'];
    const index = author.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: isFullSize ? { xs: 3, md: 4 } : { xs: 2.5, md: 3 },
        backgroundColor: 'rgba(15, 51, 89, 0.4)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: 'rgba(15, 51, 89, 0.5)',
          borderColor: 'rgba(255, 255, 255, 0.15)',
        }
      }}
    >
      {/* Quote */}
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontSize: isFullSize ? { xs: '1rem', md: '1.1rem' } : { xs: '0.85rem', md: '0.95rem' },
          lineHeight: 1.6,
          mb: 'auto',
          fontStyle: 'italic',
        }}
      >
        "{quote}"
      </Typography>

      {/* Author Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
        <Avatar
          sx={{
            width: isFullSize ? 40 : 32,
            height: isFullSize ? 40 : 32,
            bgcolor: getAvatarColor(),
            fontSize: isFullSize ? '1rem' : '0.85rem',
            fontWeight: 600,
          }}
        >
          {avatar}
        </Avatar>
        <Box>
          <Typography
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: isFullSize ? '0.95rem' : '0.85rem',
            }}
          >
            {author}
          </Typography>
          {(role || company) && (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: isFullSize ? '0.85rem' : '0.75rem',
              }}
            >
              {role}{role && company ? ' at ' : ''}{company}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default TestimonialCard;